import { useState, useEffect } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import MosSmartDialog from '../common/MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import { Stack, TextField, Box, CircularProgress } from '@mui/material'
import useMosInfo from '../common/provider/useMosInfo'
import CancelPartsByQualityDialog from './CancelPartsByQualityDialog'

// Pending-only cancellation: the dialog shown while the setting "Cancel only not started parts" is ON.
const PendingCancelPartsDialog = ({ open, onCancel, onSuccess, fabricationOrder, jobReference, maxQuantity = 0 }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker } = useMosInfo()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  // Reset the form and clear any stale (non-auto-dismissing) error alert each time the dialog opens.
  useEffect(() => {
    if (open) {
      setQuantity('')
      setReason('')
      setLoading(false)
      dismiss()
    }
  }, [open, dismiss])

  const accept = async () => {
    const requestedQuantity = Number(quantity)
    // V1: positive integer only (no zero / negative).
    if (!(requestedQuantity > 0)) {
      showAlert({ severity: 'warning', title: t_('Quantity must be greater than 0') })
      return
    }
    // V2: cannot cancel more than the pending parts of the active job (remaining).
    if (requestedQuantity > maxQuantity) {
      showAlert({ severity: 'warning', title: t_('Cannot cancel more parts than pending in the active job') })
      return
    }
    setLoading(true)
    try {
      await apiPost('mos/parts/cancel/' + worker.id + '/' + device.id + '/' + fabricationOrder.id + '/' + jobReference.id, { quantity: Number(quantity), reason })
      if (onSuccess) onSuccess()
      onCancel()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Cancel parts') })}
      setOpen={open}
      cancelCallback={onCancel}
      acceptCallback={accept}
      disableButtons={loading}
    >
      {AlertElement}
      <Stack spacing={2} sx={{ mt: 1, p: 2 }}>
        <TextField
          fullWidth
          type='number'
          value={quantity}
          label={t_('Quantity to cancel')}
          onChange={(e) => setQuantity(e.target.value)}
          size='small'
          sx={{ mt: 1 }}
          inputProps={{ min: 1, max: maxQuantity }}
          helperText={t_('Remaining') + ': ' + maxQuantity}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          value={reason}
          label={t_('Cancellation reason (optional)')}
          onChange={(e) => setReason(e.target.value)}
          size='small'
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, visibility: loading ? 'visible' : 'hidden' }}>
          <CircularProgress size={24} />
        </Box>
      </Stack>
    </MosSmartDialog>
  )
}

// The backend preview decides which dialog applies: with the setting "Cancel only not started parts" OFF it
// answers reportedPartsCancellable = true and the user can also cancel OK and NOK parts. Until it answers
// (or if it fails) the pending-only dialog is shown, exactly as before.
const CancelPartsDialog = (props) => {
  const { open, fabricationOrder, jobReference } = props
  const [preview, setPreview] = useState(undefined)
  const fabricationOrderId = fabricationOrder?.id
  const jobReferenceId = jobReference?.id

  useEffect(() => {
    if (!open || fabricationOrderId === undefined || jobReferenceId === undefined) return undefined
    let active = true
    Promise.resolve()
      .then(() => apiGet('mos/parts/cancel/preview/' + fabricationOrderId + '/' + jobReferenceId))
      .then((response) => {
        if (active) setPreview(response?.reportedPartsCancellable === true ? response : undefined)
      })
      .catch(() => {
        if (active) setPreview(undefined)
      })
    return () => {
      active = false
    }
  }, [open, fabricationOrderId, jobReferenceId])

  if (preview) {
    return <CancelPartsByQualityDialog {...props} preview={preview} />
  }
  return <PendingCancelPartsDialog {...props} />
}

export default CancelPartsDialog
